import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useApiTranslations} from '../../../../context/ApiTranslationsContext';
import {
  LicenseOrganizationController
} from '../../../../api/license/license-organization.controller';
import {FeatureTemplate} from '../../../../model/license/feature-template.model';
import {LicenseDetails} from '../../../../model/license/license-details.model';
import {LicenseFeatureDetails} from '../../../../model/license/license-feature-details.model';
import {GrantLogEntry} from '../../../../model/license/grant-log-entry.model';
import {OrganizationData} from '../../../../model/organization/organization-data';
import {SortingUtils} from '../../../../utils/sorting.utils';
import {
  OrganizationDetailsPageHooks,
} from './organization-details-page-hooks';

export type MassiveAction = 'no_action' | 'update_status' | 'update_subscription_type' | 'update_trial_date' | 'create_plan'

export type OrganizationDetailsPageState = {
  organizationDetails: OrganizationData | null
  licenseDetails: LicenseDetails | null
  grantLog: GrantLogEntry[]

  isSaveDisabled: boolean
  massiveAction: MassiveAction
  setMassiveAction: (massiveAction: MassiveAction) => void

  featureStatusChanged: (id: string) => void
  featureSubscriptionTypeChanged: (id: string) => void
  featureTrialDateChanged: (id: string, trialDate: string) => void
  updateFeatures: () => Promise<void>

  mainTableRows: FeatureTemplate[]
  getChildRows: (row: FeatureTemplate) => FeatureTemplate[];
  getFeatureDetails: (featureTemplateId: string) => LicenseFeatureDetails | null
  isFeatureTemplateSelected: (id: string) => boolean
  isFeatureEnabledForPlanCreation: (id: string) => boolean
  onFeatureTemplateSelectionChanged: (id: string, checked: boolean) => void

  createOrganizationLicensePlanDrawerOpen: boolean
  setCreateOrganizationLicensePlanDrawerOpen: (value: boolean) => void
  createLicensePlan: (subscriptionType: string) => Promise<void>
  recalculateLicense: () => Promise<void>
  updateDiscount: (discount: number) => Promise<void>

  translate: (key: string) => string
  translateLocal: (key: string) => string
}

export const useOrganizationDetailsPageState = (organizationId: string): OrganizationDetailsPageState => {
  const {t} = useTranslation()
  const {translate: apiTranslate} = useApiTranslations()

  const [createOrganizationLicensePlanDrawerOpen, setCreateOrganizationLicensePlanDrawerOpen] = useState(false);

  const [massiveAction, setMassiveAction] = useState<MassiveAction>('no_action')
  const [licenseDetails, setLicenseDetails] = OrganizationDetailsPageHooks.useLicenseDetails(organizationId);
  const [featureTemplates, __] = OrganizationDetailsPageHooks.useFeatureTemplates();
  const [licenseFeatureDetails, setLicenseFeatureDetails] = OrganizationDetailsPageHooks.useLicenseFeatureDetails(organizationId);
  const [organizationData, ] = OrganizationDetailsPageHooks.useOrganizationDetails(organizationId)
  const [grantLog, setGrantLog] = OrganizationDetailsPageHooks.useGrantLog(organizationId);
  const [modifiedFeatureIds, setModifiedFeatureIds] = useState<string[]>([])
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<string[]>([])

  const refreshLicenseFeatureDetails = async () => {
    setLicenseFeatureDetails(await OrganizationDetailsPageHooks.fetchLicenseFeatureDetails(organizationId))
    setGrantLog(await OrganizationDetailsPageHooks.fetchGrantLog(organizationId))
  }

  const refreshLicenseDetails = async () => {
    setLicenseDetails(await OrganizationDetailsPageHooks.fetchLicenseDetails(organizationId))
    setGrantLog(await OrganizationDetailsPageHooks.fetchGrantLog(organizationId))
  }

  const refreshAfterGrant = async () => {
    // nadanie zmienia funkcjonalnosci i koszt licencji, oraz dopisuje wpis do logu
    await refreshLicenseDetails()
    await refreshLicenseFeatureDetails()

    setSelectedFeatureIds([])
    setMassiveAction('no_action')
  }

  const featureStatusChanged = (id: string) => {
    let updatedFeatureStatus = false;
    const updated = licenseFeatureDetails.map(it => {
      if(it.featureTemplateId === id) {
        it.featureStatus = (it.featureStatus === 'ACTIVE' ? 'NOT_ACTIVE' : 'ACTIVE')
        updatedFeatureStatus = true;
      }
      return it
    })
    if(updatedFeatureStatus) {
      setModifiedFeatureIds([...modifiedFeatureIds, id])
    }
    setLicenseFeatureDetails(updated)
  }

  const featureSubscriptionTypeChanged = (id: string) => {
    let updatedFeatureSubscriptionType = false;
    const updated = licenseFeatureDetails.map(it => {
      if(it.featureTemplateId === id) {
        it.subscriptionType = (it.subscriptionType === 'PAID' ? 'TRIAL' : 'PAID')
        updatedFeatureSubscriptionType = true;
      }
      return it
    })
    if(updatedFeatureSubscriptionType) {
      setModifiedFeatureIds([...modifiedFeatureIds, id])
    }
    setLicenseFeatureDetails(updated)
  }

  const featureTrialDateChanged = (id: string, trialDate: string) => {
    let updatedFeatureTrialDate = false;
    const updated = licenseFeatureDetails.map(it => {
      if(it.featureTemplateId === id) {
        it.trialExpiredDate = trialDate
        updatedFeatureTrialDate = true;
      }
      return it
    })
    if(updatedFeatureTrialDate) {
      setModifiedFeatureIds([...modifiedFeatureIds, id])
    }
    setLicenseFeatureDetails(updated)
  }

  const updateFeatures = async () => {
    const entries = licenseFeatureDetails
      .filter(it => modifiedFeatureIds.includes(it.featureTemplateId))
      .map(it => ({
        featureLicenseId: it.id,
        featureTemplateId: it.featureTemplateId,
        status: it.featureStatus,
        trialExpiredDate: it.trialExpiredDate,
        subscriptionType: it.subscriptionType,
        version: it.version
      }))

    if(entries.length > 0) {
      await LicenseOrganizationController.updateLicenseFeatureStatus(organizationId, entries)
    }
    await refreshLicenseFeatureDetails()
    await refreshLicenseDetails()

    setModifiedFeatureIds([])
    setMassiveAction('no_action')
  }

  const createLicensePlan = async (subscriptionType: string) => {
    await LicenseOrganizationController.grantFeatures(organizationId, {
      subscriptionType: subscriptionType,
      featureTemplateIds: selectedFeatureIds
    })
    await refreshAfterGrant()
  }

  // Rekalkulacja ceny licencji wg biezacych cen katalogu; odswiezamy naglowek (nowy koszt + rabat).
  const recalculateLicense = async () => {
    await LicenseOrganizationController.recalculateLicense(organizationId)
    await refreshLicenseDetails()
  }

  const updateDiscount = async (discount: number) => {
    if (!licenseDetails) return;
    await LicenseOrganizationController.updateOrganizationLicense(organizationId, {
      licenseType: licenseDetails.licenseType,
      discount: discount,
      licenseExpiredDate: licenseDetails.licenseExpiredDate,
    })
    await refreshLicenseDetails()
  }

  return {
    createLicensePlan: createLicensePlan,
    recalculateLicense: recalculateLicense,
    updateDiscount: updateDiscount,
    isFeatureEnabledForPlanCreation: (featureTemplateId) => licenseFeatureDetails.filter(it => it.featureTemplateId === featureTemplateId).length === 0,
    isFeatureTemplateSelected: (featureTemplateId) => selectedFeatureIds.includes(featureTemplateId),
    onFeatureTemplateSelectionChanged: (featureTemplateId, checked) => {
      const updated = checked
        ? [...selectedFeatureIds, featureTemplateId]
        : selectedFeatureIds.filter(it => it !== featureTemplateId)

      setSelectedFeatureIds(updated)
    },
    grantLog: grantLog,
    organizationDetails: organizationData,
    massiveAction: massiveAction,
    setMassiveAction: async (action) => {
      if(action === 'no_action') {
        await refreshLicenseFeatureDetails()
      }
      setSelectedFeatureIds([])
      setModifiedFeatureIds([])
      setMassiveAction(action)
    },
    featureStatusChanged: featureStatusChanged,
    featureSubscriptionTypeChanged: featureSubscriptionTypeChanged,
    featureTrialDateChanged: featureTrialDateChanged,
    updateFeatures: updateFeatures,
    licenseDetails: licenseDetails,
    mainTableRows: SortingUtils.sortFeatureTemplatesByModulePriority(SortingUtils.createModuleParents(featureTemplates)),
    getChildRows: (parent) => featureTemplates.filter(it => it.featureModule === parent.featureModule),
    getFeatureDetails: (featureTemplateId) => licenseFeatureDetails.find(it => it.featureTemplateId === featureTemplateId) ?? null,
    createOrganizationLicensePlanDrawerOpen: createOrganizationLicensePlanDrawerOpen,
    setCreateOrganizationLicensePlanDrawerOpen: (value) => {
      setCreateOrganizationLicensePlanDrawerOpen(value)
    },
    isSaveDisabled: (massiveAction === 'create_plan' && selectedFeatureIds.length === 0) || (massiveAction !== 'no_action' && massiveAction !== 'create_plan' && modifiedFeatureIds.length === 0),
    translate: (key) => apiTranslate(key) || t(key),
    translateLocal: (key) => t(`organizations-details.${key}`)
  }
}